import { Term } from '@src/graphql-types';
import { useUniversalState } from '@src/hooks/useUniversalState';
import { createContext, FC, useCallback, useContext, type ReactNode } from 'react';
type PerksSection = 'partners' | 'bizplan' | 'clarity' | 'fundable' | 'launchrock';
type PerksContextValue = {
  breadcrumbs?: {
    title: string;
    href: string;
  }[];
  activeSection?: PerksSection;
  activeKeywords?: string; // Added activeKeywords to the context value
  activeTerms?: Term[]; // Added activeTerms to the context value
  setBreadcrumbs?: (breadcrumbs: PerksContextValue['breadcrumbs']) => void;
  setActiveSection?: (section: PerksSection) => void;
  setActiveKeywords?: (keywords: string) => void; // Added setActiveKeywords method
  setActiveTerms?: (terms: Term[]) => void; // Added setActiveTerms method
  addActiveTerm?: (term: Term) => void; // Added addActiveTerm method
  removeActiveTerm?: (term: Term) => void; // Added removeActiveTerm method
};
export const PerksContext = createContext<PerksContextValue>({});
export const usePerksContext = () => {
  return useContext(PerksContext);
};
export const PerksContextProvider: FC<PerksContextValue & {
  children?: ReactNode;
}> = props => {
  const [breadcrumbs, setBreadcrumbs] = useUniversalState<PerksContextValue['breadcrumbs']>('PerksContext.breadcrumbs', props.breadcrumbs || [{
    title: 'Perks',
    href: '/admin'
  }]);
  const [activeSection, setActiveSection] = useUniversalState<PerksSection>('PerksContext.activeSection', props.activeSection || 'partners');
  const [activeKeywords, setActiveKeywords] = useUniversalState<string>('PerksContext.activeKeywords', props.activeKeywords || ''); // State for activeKeywords
  const [activeTerms, setActiveTerms] = useUniversalState<Term[]>('PerksContext.activeTerms', props.activeTerms || []); // State for activeTerms

  const addActiveTerm = useCallback((term: Term) => {
    setActiveTerms(prevTerms => [...prevTerms, term]);
  }, []);
  const removeActiveTerm = useCallback((term_0: Term) => {
    setActiveTerms(prevTerms_0 => prevTerms_0.filter(t => t.slug !== term_0.slug));
  }, []);
  const contextValue = {
    breadcrumbs,
    activeSection,
    activeKeywords,
    activeTerms,
    setBreadcrumbs,
    setActiveSection,
    setActiveKeywords,
    setActiveTerms,
    addActiveTerm,
    removeActiveTerm
  };
  return <PerksContext.Provider value={contextValue}>{props.children}</PerksContext.Provider>;
};